import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Role, Permission, Application } from "@/contracts/auth-api.types";
import { roleService } from "@/services/roleService";
import { applicationService } from "@/services/applicationService";
import { useNotification } from "@/hooks/useNotification";
import { Button } from "@/components/common/Button";
import { Badge } from "@/components/common/Badge";
import { PermissionGuard } from "@/components/guards/PermissionGuard";
import {
  Plus,
  Shield,
  Settings2,
  RefreshCw,
  KeyRound,
  Search,
  Filter,
  CheckCircle2,
  Calendar,
  Lock,
  Layers,
  Users,
  FileText,
  HelpCircle,
} from "lucide-react";
import { formatDate, getErrorMessage } from "@/utils/formatters";
import { RoleModal } from "./RoleModal";
import { RolePermissionsModal } from "./RolePermissionsModal";

export const RolesList: React.FC = () => {
  const { notify } = useNotification();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissionsCatalog, setPermissionsCatalog] = useState<Permission[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [roleToManage, setRoleToManage] = useState<Role | null>(null);

  // Filtros do Catálogo de Permissões
  const [permSearch, setPermSearch] = useState("");
  const [selectedDomain, setSelectedDomain] = useState<string>("all");

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [rolesList, permsList, appsRes] = await Promise.all([
        roleService.listRoles(),
        roleService.listPermissions(),
        applicationService.list(1, 100),
      ]);
      setRoles(rolesList || []);
      setPermissionsCatalog(permsList || []);
      const appList = Array.isArray(appsRes) ? appsRes : appsRes.data || [];
      setApplications(appList);
    } catch (err: unknown) {
      notify(getErrorMessage(err, "Erro ao carregar roles e permissões"), "error");
    } finally {
      setIsLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const applicationMap = useMemo(
    () => new Map(applications.map((app) => [app.id, app])),
    [applications],
  );

  const currentApp = useMemo(
    () => applications.find((app) => app.id === selectedTenantId),
    [applications, selectedTenantId],
  );

  const displayedRoles = useMemo(() => {
    if (selectedTenantId === "all") return roles;
    return roles.filter((r) => r.application_id === selectedTenantId);
  }, [roles, selectedTenantId]);

  const tenantPermissions = useMemo(() => {
    if (selectedTenantId === "all") return permissionsCatalog;
    return permissionsCatalog.filter((p) => p.application_id === selectedTenantId);
  }, [permissionsCatalog, selectedTenantId]);

  // Extrair domínios de permissões únicos para filtro por abas (ex: applications, users, roles, audit)
  const permissionDomains = useMemo(() => {
    const domains = new Set<string>();
    tenantPermissions.forEach((p) => {
      const domain = p.name.includes(":") ? p.name.split(":")[0] : "outros";
      domains.add(domain);
    });
    return Array.from(domains).sort((a, b) => a.localeCompare(b));
  }, [tenantPermissions]);

  // Filtragem combinada do catálogo por busca de texto e domínio
  const filteredPermissions = useMemo(() => {
    return tenantPermissions.filter((perm) => {
      const domain = perm.name.includes(":") ? perm.name.split(":")[0] : "outros";
      const matchesDomain = selectedDomain === "all" || domain === selectedDomain;
      const term = permSearch.toLowerCase().trim();
      const matchesSearch =
        !term ||
        perm.name.toLowerCase().includes(term) ||
        (perm.description && perm.description.toLowerCase().includes(term));
      return matchesDomain && matchesSearch;
    });
  }, [tenantPermissions, selectedDomain, permSearch]);

  const getDomainIcon = (domain: string) => {
    switch (domain) {
      case "applications":
        return <Layers size={14} className="text-blue-600" />;
      case "users":
        return <Users size={14} className="text-green-600" />;
      case "roles":
        return <Shield size={14} className="text-purple-600" />;
      case "audit":
        return <FileText size={14} className="text-amber-600" />;
      case "permissions":
        return <KeyRound size={14} className="text-primary" />;
      default:
        return <Lock size={14} className="text-muted" />;
    }
  };

  const getActionBadgeVariant = (
    action: string,
  ): "success" | "danger" | "warning" | "info" | "neutral" => {
    switch (action) {
      case "create":
        return "success";
      case "delete":
        return "danger";
      case "update":
        return "warning";
      case "list":
      case "read":
        return "info";
      default:
        return "neutral";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold text-gray-900">Roles & Permissões RBAC</h1>
            <Badge variant="neutral" className="text-xs font-mono">
              IAM Engine
            </Badge>
          </div>
          <p className="text-muted text-sm mt-1">
            Defina papéis hierárquicos e vincule permissões canônicas operacionais no padrão{" "}
            <code className="font-mono text-primary bg-blue-50 px-1 py-0.5 rounded text-xs">
              recurso:acao
            </code>
            .
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            isLoading={isLoading}
            title="Recarregar perfis e permissões"
          >
            <RefreshCw size={15} />
            <span className="hide-sm">Recarregar</span>
          </Button>

          <PermissionGuard permission="roles:create">
            <Button variant="primary" size="sm" onClick={() => setIsRoleModalOpen(true)}>
              <Plus size={16} />
              <span>Nova Role</span>
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Tenant Context Selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-3.5 flex flex-wrap items-center justify-between gap-4 shadow-2xs">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded bg-blue-50 text-blue-600 shrink-0">
            <Layers size={18} />
          </div>
          <div>
            <label
              htmlFor="roles-tenant-filter"
              className="text-xs font-semibold text-gray-700 uppercase tracking-wider block"
            >
              Tenant / Aplicação Ativa
            </label>
            <p className="text-xs text-muted">
              Isole o controle de roles e permissões para evitar vazamento entre tenants.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            id="roles-tenant-filter"
            aria-label="Selecionar Tenant"
            className="input-control text-xs font-medium py-1.5 px-3 min-w-[240px]"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
          >
            <option value="all">Todos os Tenants ({roles.length} roles)</option>
            {applications.map((app) => {
              const count = roles.filter((r) => r.application_id === app.id).length;
              return (
                <option key={app.id} value={app.id}>
                  {app.name} ({app.client_id}) — {count} roles
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {selectedTenantId !== "all" && currentApp && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between text-xs text-blue-900">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-blue-600 shrink-0" />
            <span>
              Visualizando governança RBAC isolada do Tenant: <strong>{currentApp.name}</strong> (
              <code>{currentApp.client_id}</code>). Nenhuma configuração de outro tenant é
              compartilhada aqui.
            </span>
          </div>
          <button
            type="button"
            className="text-xs text-blue-700 underline font-medium hover:text-blue-800"
            onClick={() => setSelectedTenantId("all")}
          >
            Ver todos os tenants
          </button>
        </div>
      )}

      {/* KPI Overview Pills */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div className="card p-3.5 flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-purple-50 text-purple-600 shrink-0">
            <Shield size={20} />
          </div>
          <div>
            <p className="text-xs font-semibold text-muted uppercase tracking-wider">
              Perfis Ativos
            </p>
            <p className="text-lg font-bold text-gray-900">
              {displayedRoles.length} roles cadastradas
            </p>
          </div>
        </div>

        <div className="card p-3.5 flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-blue-50 text-blue-600 shrink-0">
            <KeyRound size={20} />
          </div>
          <div>
            <p className="text-xs font-semibold text-muted uppercase tracking-wider">
              Catálogo de Ações
            </p>
            <p className="text-lg font-bold text-gray-900">
              {tenantPermissions.length} permissões canônicas
            </p>
          </div>
        </div>

        <div className="card p-3.5 flex items-center gap-3 sm:col-span-2 md:col-span-1">
          <div className="p-2.5 rounded-lg bg-green-50 text-green-600 shrink-0">
            <CheckCircle2 size={20} />
          </div>
          <div>
            <p className="text-xs font-semibold text-muted uppercase tracking-wider">
              Domínios Mapeados
            </p>
            <p className="text-lg font-bold text-gray-900">
              {permissionDomains.length} escopos ativos
            </p>
          </div>
        </div>
      </div>

      {/* Roles Grid Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Shield size={18} className="text-primary" />
            <span>
              Perfis de Acesso{" "}
              {selectedTenantId !== "all"
                ? `do Tenant (${displayedRoles.length})`
                : `(${displayedRoles.length})`}
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {isLoading && (
            <div className="col-span-full py-12 text-center card">
              <div className="spinner mx-auto mb-3"></div>
              <p className="text-sm text-muted">Carregando roles cadastradas...</p>
            </div>
          )}

          {!isLoading && displayedRoles.length === 0 && (
            <div className="col-span-full py-12 text-center text-muted card">
              <Shield size={32} className="mx-auto mb-2 text-muted opacity-50" />
              <p className="text-base font-semibold text-gray-800">
                Nenhuma role cadastrada para este tenant.
              </p>
              <p className="text-xs text-muted mt-1">
                Crie um perfil como ADMIN ou OPERATOR para conceder privilégios aos usuários.
              </p>
            </div>
          )}

          {!isLoading &&
            displayedRoles.map((role) => {
              const isAdmin = role.name === "ADMIN";
              const permsCount = role.permissions ? role.permissions.length : 0;
              const appInfo = applicationMap.get(role.application_id);

              return (
                <div key={role.id} className="role-card card flex flex-col justify-between">
                  <div>
                    {/* Role Card Header */}
                    <div className="flex items-start justify-between gap-2 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2.5 rounded-lg shrink-0 ${
                            isAdmin
                              ? "bg-purple-50 text-purple-600 border border-purple-100"
                              : "bg-blue-50 text-blue-600 border border-blue-100"
                          }`}
                        >
                          <Shield size={22} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-base text-gray-900 font-mono tracking-wide">
                              {role.name}
                            </h3>
                            {isAdmin && (
                              <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                Mestre
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className="flex items-center gap-1 text-xs text-muted">
                              <Calendar size={12} />
                              <span>{formatDate(role.created_at)}</span>
                            </span>
                            <Badge variant="neutral" className="text-[10px] font-mono py-0">
                              {appInfo ? `${appInfo.name}` : role.application_id || "auth-admin"}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <Badge
                        variant={permsCount > 0 ? "success" : "neutral"}
                        className="text-xs px-2.5 py-0.5 shrink-0"
                      >
                        {permsCount} {permsCount === 1 ? "perm" : "perms"}
                      </Badge>
                    </div>

                    {/* Role Description */}
                    <div className="my-3 min-h-[36px]">
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {role.description || (
                          <span className="italic text-muted">
                            Nenhuma descrição informada para esta role.
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Assigned Permissions Chips */}
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-[11px] font-semibold text-muted uppercase tracking-wider mb-2">
                        Amostra de Permissões:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {role.permissions && role.permissions.length > 0 ? (
                          <>
                            {role.permissions.slice(0, 4).map((p) => {
                              return (
                                <span
                                  key={p.id}
                                  className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-mono border border-gray-200"
                                >
                                  {p.name}
                                </span>
                              );
                            })}
                            {role.permissions.length > 4 && (
                              <span className="text-xs text-primary font-medium self-center px-1">
                                +{role.permissions.length - 4} mais
                              </span>
                            )}
                          </>
                        ) : (
                          <div className="w-full py-2 px-2.5 rounded bg-gray-50 border border-dashed border-gray-200 text-center">
                            <span className="text-xs text-muted">
                              Nenhuma permissão associada a este perfil
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="pt-4 mt-4 border-t border-gray-100">
                    <PermissionGuard permission="roles:update">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full flex items-center justify-center gap-2 hover:border-gray-300"
                        onClick={() => setRoleToManage(role)}
                      >
                        <Settings2 size={15} />
                        <span>Gerenciar Permissões</span>
                      </Button>
                    </PermissionGuard>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Permissions Catalog Section - High-End RBAC Explorer */}
      <div className="card space-y-4">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <KeyRound size={20} className="text-primary" />
              <span>Catálogo de Permissões Disponíveis ({permissionsCatalog.length})</span>
            </h2>
            <p className="text-xs text-muted mt-0.5">
              Conjunto de permissões no padrão canônico{" "}
              <code className="text-primary font-mono font-semibold">recurso:acao</code> suportadas
              pelo backend da Auth API.
            </p>
          </div>

          {/* Quick Search */}
          <div className="w-full sm:w-72">
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Filtrar permissão ou ação..."
                value={permSearch}
                onChange={(e) => setPermSearch(e.target.value)}
                className="search-input text-xs py-1.5"
              />
              {permSearch && (
                <button
                  type="button"
                  onClick={() => setPermSearch("")}
                  className="btn-icon text-xs text-muted mr-2"
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Domain Filter Pills */}
        <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-gray-100">
          <span className="text-xs font-semibold text-muted mr-1 flex items-center gap-1">
            <Filter size={12} />
            <span>Módulo:</span>
          </span>
          <button
            type="button"
            className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${
              selectedDomain === "all"
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            onClick={() => setSelectedDomain("all")}
          >
            Todos ({permissionsCatalog.length})
          </button>
          {permissionDomains.map((domain) => {
            const count = permissionsCatalog.filter(
              (p) => (p.name.includes(":") ? p.name.split(":")[0] : "outros") === domain,
            ).length;

            return (
              <button
                key={domain}
                type="button"
                className={`px-2.5 py-1 text-xs rounded-full font-medium flex items-center gap-1.5 transition-colors ${
                  selectedDomain === domain
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                onClick={() => setSelectedDomain(domain)}
              >
                <span>{domain}</span>
                <span className="opacity-75 font-mono text-[11px]">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Permissions Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2">
          {filteredPermissions.length === 0 && (
            <div className="col-span-full py-8 text-center text-muted bg-gray-50 rounded border border-gray-200">
              <HelpCircle size={24} className="mx-auto mb-2 text-muted opacity-50" />
              <p className="text-sm font-medium">
                Nenhuma permissão encontrada com o filtro aplicado.
              </p>
              <button
                type="button"
                onClick={() => {
                  setPermSearch("");
                  setSelectedDomain("all");
                }}
                className="text-xs text-primary font-semibold hover:underline mt-2 inline-block"
              >
                Limpar todos os filtros
              </button>
            </div>
          )}

          {filteredPermissions.length > 0 &&
            filteredPermissions.map((perm) => {
              const parts = perm.name.split(":");
              const domain = parts[0] || "geral";
              const action = parts[1] || "";

              return (
                <div
                  key={perm.id}
                  className="p-3 bg-white border border-gray-200 rounded-lg flex flex-col justify-between hover:border-blue-300 hover:shadow-xs transition-all"
                >
                  <div>
                    <div className="flex items-center justify-between gap-1 mb-2">
                      <div className="flex items-center gap-1.5">
                        {getDomainIcon(domain)}
                        <span className="text-[11px] font-semibold text-gray-600 uppercase">
                          {domain}
                        </span>
                      </div>
                      {action && (
                        <Badge
                          variant={getActionBadgeVariant(action)}
                          className="text-[10px] py-0 px-1.5 font-mono"
                        >
                          {action}
                        </Badge>
                      )}
                    </div>

                    <div className="my-1">
                      <code className="text-xs font-semibold text-primary font-mono block break-all">
                        {perm.name}
                      </code>
                    </div>

                    <p className="text-xs text-muted mt-1 leading-snug line-clamp-2">
                      {perm.description || "Permissão operacional canônica"}
                    </p>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Create Role Modal */}
      <RoleModal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        onSuccess={loadData}
        defaultApplicationId={selectedTenantId !== "all" ? selectedTenantId : undefined}
        applications={applications}
      />

      {/* Manage Role Permissions Modal */}
      <RolePermissionsModal
        isOpen={!!roleToManage}
        onClose={() => setRoleToManage(null)}
        role={roleToManage}
        tenantName={
          roleToManage
            ? applicationMap.get(roleToManage.application_id)?.name || roleToManage.application_id
            : undefined
        }
        onSuccess={loadData}
      />
    </div>
  );
};
